import {AxiosInstance} from "axios";
import {TeamTask, TeamTaskComment, TeamTaskPriority, TeamTaskStatus} from "@/core/interfaces/TeamTask";

export interface TeamTaskStoreRequest {
    title: string;
    description?: string | null;
    status?: TeamTaskStatus;
    priority?: TeamTaskPriority;
    category?: string | null;
    due_date?: string | null;
    customer_id?: number | null;
    /** Access point ids. An empty array leaves the task unassigned. */
    assignees?: number[];
}

export type TeamTaskUpdateRequest = Partial<TeamTaskStoreRequest>;

export interface TeamTaskIndexFilters {
    status?: TeamTaskStatus;
    assignee_id?: number;
    due_before?: string;
    due_after?: string;
    archived?: boolean;
    limit?: number;
}

/**
 * The team task board. Lives on the HDS: the title and the description are
 * free text where the pharmacist may write patient data.
 */
const TeamTaskResource = (hdsClient: AxiosInstance) => {
    return {
        /**
         * Index the board of an organization. Archived tasks are excluded
         * unless the filter asks for them.
         */
        async index(organizationId: number, filters: TeamTaskIndexFilters = {}): Promise<TeamTask[]> {
            const response = await hdsClient.get(
                `organizations/${organizationId}/team-tasks`,
                {params: filters}
            );

            return response.data.data;
        },

        /**
         * Create a task for the organization.
         */
        async create(organizationId: number, request: TeamTaskStoreRequest): Promise<TeamTask> {
            const response = await hdsClient.post(
                `organizations/${organizationId}/team-tasks`,
                {data: request}
            );

            return response.data.data;
        },

        /**
         * The thread of a task, oldest first.
         */
        async comments(taskId: number): Promise<TeamTaskComment[]> {
            const response = await hdsClient.get(`team-tasks/${taskId}/comments`);

            return response.data.data;
        },

        /**
         * Post a comment. Mentions travel inside the body as `@[id]` tokens;
         * the server reads them back from the text itself. The author is named
         * explicitly: a Connect workstation is shared by the whole team, so the
         * session says which pharmacy, never which person.
         */
        async addComment(
            taskId: number,
            body: string,
            authorAccessPointId: number
        ): Promise<TeamTaskComment> {
            const response = await hdsClient.post(`team-tasks/${taskId}/comments`, {
                data: {body, author_access_point_id: authorAccessPointId}
            });

            return response.data.data;
        },

        /**
         * Archive every finished task of the organization. Answers with the
         * number archived.
         */
        async archiveCompleted(organizationId: number): Promise<number> {
            const response = await hdsClient.post(
                `organizations/${organizationId}/team-tasks/archive-completed`
            );

            return response.data.data.archived_count;
        },

        /**
         * Put an archived task back on the board, in the column it left.
         */
        async restore(taskId: number): Promise<TeamTask> {
            const response = await hdsClient.patch(`team-tasks/${taskId}/restore`);

            return response.data.data;
        },

        /**
         * Set the order of one column, naming its whole content in the wanted
         * order. Any task in the list that sat in another column moves in.
         * Answers with the column as stored.
         */
        async reorder(organizationId: number, status: TeamTaskStatus, taskIds: number[]): Promise<TeamTask[]> {
            const response = await hdsClient.put(
                `organizations/${organizationId}/team-tasks/order`,
                {data: {status, task_ids: taskIds}}
            );

            return response.data.data;
        },

        /**
         * Update a task: its fields, its status and its assignees.
         */
        async update(taskId: number, request: TeamTaskUpdateRequest): Promise<TeamTask> {
            const response = await hdsClient.patch(`team-tasks/${taskId}`, {data: request});

            return response.data.data;
        }
    };
};

export default TeamTaskResource;
